import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { 
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue 
} from "@/components/ui/select";
import { 
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow 
} from "@/components/ui/table";
import { 
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger 
} from "@/components/ui/dialog";
import { School, AlertTriangle, Check, X, Mail, Eye, Download, Filter } from 'lucide-react';
import { toast } from 'sonner';

const schoolOrders = [
  { id: 1, name: 'Raffles Girls\' School', level: 'Sec', budget: 3000, budgetEntered: true, nfEl: '30 ✓', mtl: '48 ✓', fiction: '7/60', addlQty: 3, totalBooks: 91, budgetUsed: 2847.50, status: 'submitted' },
  { id: 2, name: 'ACS (Independent)', level: 'Sec', budget: 3000, budgetEntered: true, nfEl: '30 ✓', mtl: '48 ✓', fiction: '9/60', addlQty: 5, totalBooks: 95, budgetUsed: 2980.00, status: 'submitted' },
  { id: 3, name: 'Nanyang Primary', level: 'Pri', budget: 3000, budgetEntered: true, nfEl: '25 ✓', mtl: '55 ✓', fiction: '12/60', addlQty: 0, totalBooks: 92, budgetUsed: 2650.00, status: 'submitted' },
  { id: 4, name: 'Victoria Junior College', level: 'Pre-U', budget: 3000, budgetEntered: true, nfEl: '50 ✓', mtl: '23 ✓', fiction: '10/60', addlQty: 2, totalBooks: 88, budgetUsed: 2870.00, status: 'submitted' },
  { id: 5, name: 'Bedok Green Secondary', level: 'Sec', budget: 3000, budgetEntered: true, nfEl: '30 ✓', mtl: '48 ✓', fiction: '5/60', addlQty: 0, totalBooks: 83, budgetUsed: 2320.00, status: 'draft' },
  { id: 6, name: 'Tampines Primary', level: 'Pri', budget: 3000, budgetEntered: false, nfEl: '—', mtl: '—', fiction: '—', addlQty: 0, totalBooks: 0, budgetUsed: 0, status: 'not_started' },
  { id: 7, name: 'Woodlands Secondary', level: 'Sec', budget: 3000, budgetEntered: false, nfEl: '—', mtl: '—', fiction: '—', addlQty: 0, totalBooks: 0, budgetUsed: 0, status: 'not_started' },
];

const getStatusBadge = (status) => {
  const config = {
    submitted: { icon: Check, label: 'Submitted', className: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
    draft: { icon: null, label: 'Draft', className: 'bg-amber-50 text-amber-700 border-amber-200' },
    not_started: { icon: X, label: 'Not Started', className: 'bg-red-50 text-red-700 border-red-200' },
  };
  const c = config[status];
  return (
    <Badge variant="outline" className={c.className}>
      {c.icon && <c.icon className="w-3 h-3 mr-1" />}
      {c.label}
    </Badge>
  );
};

export default function MOESchoolMonitor() {
  const [selectedSchool, setSelectedSchool] = useState(null);

  const handleRemindNotStarted = () => {
    toast.success('Reminder sent to 27 not-started schools');
  };

  const handleRemindDraft = () => {
    toast.success('Reminder sent to 12 draft schools');
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900 flex items-center gap-3">
            <School className="w-7 h-7 text-[#1A3C6E]" />
            School Selections Monitor & Oversight
          </h1>
          <p className="text-slate-500 mt-1">CDS Cycle 1 2028 | Selection window: 15 Aug–15 Sep 2026</p>
        </div>
        <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 px-4 py-2">
          <AlertTriangle className="w-4 h-4 mr-2" />
          5 days left
        </Badge>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card className="bg-slate-50">
          <CardContent className="pt-4 pb-4">
            <div className="text-2xl font-bold text-slate-900">287</div>
            <div className="text-sm text-slate-500">Total Schools</div>
          </CardContent>
        </Card>
        <Card className="bg-emerald-50">
          <CardContent className="pt-4 pb-4">
            <div className="text-2xl font-bold text-emerald-700">248</div>
            <div className="text-sm text-emerald-600">Submitted (86%)</div>
          </CardContent>
        </Card>
        <Card className="bg-amber-50">
          <CardContent className="pt-4 pb-4">
            <div className="text-2xl font-bold text-amber-700">12</div>
            <div className="text-sm text-amber-600">Draft</div>
          </CardContent>
        </Card>
        <Card className="bg-red-50">
          <CardContent className="pt-4 pb-4">
            <div className="text-2xl font-bold text-red-700">27</div>
            <div className="text-sm text-red-600">Not Started</div>
          </CardContent>
        </Card>
        <Card className="bg-blue-50">
          <CardContent className="pt-4 pb-4">
            <div className="text-2xl font-bold text-blue-700">39</div>
            <div className="text-sm text-blue-600">Auto-reminded</div>
          </CardContent>
        </Card>
      </div>

      {/* Bulk Actions */}
      <div className="flex flex-wrap items-center gap-4">
        <Button 
          variant="outline" 
          className="text-red-600 hover:text-red-700 hover:bg-red-50"
          onClick={handleRemindNotStarted}
          data-testid="remind-not-started"
        >
          <Mail className="w-4 h-4 mr-2" />
          Send Reminder to 27 Not-Started Schools
        </Button>
        <Button 
          variant="outline" 
          className="text-amber-600 hover:text-amber-700 hover:bg-amber-50"
          onClick={handleRemindDraft}
          data-testid="remind-draft"
        >
          <Mail className="w-4 h-4 mr-2" />
          Send Reminder to 12 Draft Schools
        </Button>
        <Button variant="outline">
          <Download className="w-4 h-4 mr-2" />
          Export Selections Summary Excel
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-slate-500" />
          <Select defaultValue="all">
            <SelectTrigger className="w-32">
              <SelectValue placeholder="Level" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Levels</SelectItem>
              <SelectItem value="primary">Primary</SelectItem>
              <SelectItem value="secondary">Secondary</SelectItem>
              <SelectItem value="preu">Pre-U</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Select defaultValue="all">
          <SelectTrigger className="w-32">
            <SelectValue placeholder="Cluster" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Clusters</SelectItem>
            <SelectItem value="central">Central</SelectItem>
            <SelectItem value="east">East</SelectItem>
            <SelectItem value="west">West</SelectItem>
            <SelectItem value="north">North</SelectItem>
          </SelectContent>
        </Select>
        <Select defaultValue="all">
          <SelectTrigger className="w-32">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="submitted">Submitted</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="not_started">Not Started</SelectItem>
          </SelectContent>
        </Select>
        <Input placeholder="Search school..." className="w-64" />
      </div>

      {/* Schools Table */}
      <Card>
        <CardContent className="pt-6">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>School</TableHead>
                <TableHead>Level</TableHead>
                <TableHead>Budget</TableHead>
                <TableHead>Budget Entered</TableHead>
                <TableHead>NF EL</TableHead>
                <TableHead>MTL</TableHead>
                <TableHead>Fiction</TableHead>
                <TableHead>Add'l Qty</TableHead>
                <TableHead>Total Books</TableHead>
                <TableHead>Budget Used</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {schoolOrders.map((school) => (
                <TableRow key={school.id} className="table-row-striped">
                  <TableCell className="font-medium">{school.name}</TableCell>
                  <TableCell>{school.level}</TableCell>
                  <TableCell>${school.budget.toLocaleString()}</TableCell>
                  <TableCell>
                    {school.budgetEntered ? (
                      <Check className="w-4 h-4 text-emerald-600" />
                    ) : (
                      <X className="w-4 h-4 text-red-600" />
                    )}
                  </TableCell>
                  <TableCell>{school.nfEl}</TableCell>
                  <TableCell>{school.mtl}</TableCell>
                  <TableCell>{school.fiction}</TableCell>
                  <TableCell>{school.addlQty > 0 ? `${school.addlQty} extra` : '—'}</TableCell>
                  <TableCell>{school.totalBooks || '—'}</TableCell>
                  <TableCell>{school.budgetUsed > 0 ? `$${school.budgetUsed.toLocaleString()}` : '—'}</TableCell>
                  <TableCell>{getStatusBadge(school.status)}</TableCell>
                  <TableCell>
                    {school.status === 'submitted' ? (
                      <Dialog>
                        <DialogTrigger asChild>
                          <Button size="sm" variant="outline" onClick={() => setSelectedSchool(school)}>
                            <Eye className="w-3 h-3 mr-1" /> View Order
                          </Button>
                        </DialogTrigger>
                        <DialogContent>
                          <DialogHeader>
                            <DialogTitle>{school.name} - Order Details</DialogTitle>
                            <DialogDescription>PO-{school.name.replace(/[^A-Z]/g, '').substring(0, 3)}-CDS-C1-2028</DialogDescription>
                          </DialogHeader>
                          <div className="space-y-4 pt-4">
                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <div className="text-sm text-slate-500">Total Books</div>
                                <div className="font-semibold">{school.totalBooks}</div>
                              </div>
                              <div>
                                <div className="text-sm text-slate-500">Budget Used</div>
                                <div className="font-semibold">${school.budgetUsed.toLocaleString()}</div>
                              </div>
                              <div>
                                <div className="text-sm text-slate-500">Budget Remaining</div>
                                <div className="font-semibold">${(school.budget - school.budgetUsed).toFixed(2)}</div>
                              </div>
                            </div>
                            <Button variant="outline" className="w-full">
                              <Download className="w-4 h-4 mr-2" />
                              Download School's PO PDF
                            </Button>
                          </div>
                        </DialogContent>
                      </Dialog>
                    ) : school.status === 'draft' ? (
                      <div className="flex gap-1">
                        <Button size="sm" variant="outline">View</Button>
                        <Button size="sm" variant="outline" className="text-amber-600">
                          <Mail className="w-3 h-3" />
                        </Button>
                      </div>
                    ) : (
                      <Button size="sm" variant="outline" className="text-red-600">
                        <Mail className="w-3 h-3 mr-1" /> Remind
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <p className="text-sm text-slate-500 mt-4 text-center">Showing 7 of 287 schools</p>
        </CardContent>
      </Card>
    </div>
  );
}
